/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { tqconstants } from "../utils/constants/TQConstants";
import TQFont, { tqtypes } from "./TQTypes";
import { TQLogger } from "../utils/logger/TQLogger";

/** Initialization parameters */
export default class TQInitParams {
    /** Authentication information required for Adobe Fonts */
    private _authParams: tqtypes.AuthParams;

    /** Default  mode is found fonts */
    private _runtimeMode: tqtypes.RuntimeMode = tqtypes.RuntimeMode.FOUND_FONTS;

    /** Whether the app is paid, free or freemium. Default is freemium */
    private _appEntitlement: tqtypes.AppEntitlement = tqtypes.AppEntitlement.FREEMIUM;

    /** Applications can specify the language as well, if undefined tqweb would determine this from browser */
    private static _lang: tqtypes.Language | undefined = undefined;

    private static _cachedEntitlement: tqtypes.Entitlement | undefined = undefined;

    /** Applications can specify which kind of font buffer they need to fetch, By default raw + protected font buffer is fetched */
    private _fontBufferType: tqtypes.FontBufferType = tqtypes.FontBufferType.RAW_PROTECTED;

    /* FontFetchStrategy to decide whether to enable System fonts (Google API), Adobe fonts(TypeKit server)
        or both (system fonts prefered over adobe fonts)*/
    private _fontFetchStrategy: tqtypes.FontFetchStrategy = tqtypes.FontFetchStrategy.ALL;

    /* App fonts type to be specified for findFonts workflow in RuntimeMode.FOUND_FONTS. This will be required by the Found Fonts mode
     * to additionally find the app specific fonts */
    private _applicationFontsCollectionType: tqtypes.ApplicationFontsType[] | tqtypes.ApplicationFontsType | undefined = undefined;

    /*
     * To be specified which fonts needs update from TQWeb this can be set. By default ACTIVATED would be watched for RuntimeMode browsing mode.
     * Also, by default BYOF would be watched for RuntimeMode FOUND FONTS mode.
     * Browse Mode would need to turn it off if FOUND_FONTS would be watching BYOF.
     * If TQWeb is integrated separately at 2 places i.e browsing mode and found fonts mode, only one of these mode needs to watch for the BYOF updates.
     */
    private _watchFontUpdates: tqtypes.FontType[] = [tqtypes.FontType.BYOF];

    /** Applications can specify which kind of Disk Cache they need, By default Disk Cache layer is disabled */
    private _diskCacheType: tqtypes.DiskCacheType = tqtypes.DiskCacheType.NONE;

    /* This callback needs to defined at application side to receive fonts update.
        Where they would need to call update on their cached aggregation object */
    private _fontsUpdateCallback: ((updates: tqtypes.FontUpdateNotification[]) => boolean) | undefined = undefined;
    /**
     * Sample callback definition
     * (updates: tqtypes.FontUpdateNotification[]) => {
     *      const tqFontAggregation = new TQFontAggregation() or a global aggregation
     *      tqFontAggregation.handleUpdates(updates); // Application needs to pass their aggragtion here
     *      return true;
     * };
     */

    /* Status update callback */
    private _statusUpdateCallback: (status: tqtypes.StatusCode, error: tqtypes.Error) => boolean = (status: tqtypes.StatusCode, error: tqtypes.Error) => {
        return true;
    };

    /* global font update callback */
    private _fontDownloadCallback: tqtypes.FontDownloadCallback = (fontInfo: TQFont, error: tqtypes.Error) => {
        return true;
    };

    /** TypeComp NetworkPreference for any network operations to be performed. */
    private _timeOutDurationInMillis: number = 60 * 1000;

    /** Selections poll default duration, currently 10 ticks */
    private _selectionPollInterval = tqconstants.defaultPollInterval;

    public static _Logger: any = undefined;

    public static get Logger() {
        if (TQInitParams._Logger == undefined) {
            TQInitParams._Logger = new TQLogger();
        }
        return TQInitParams._Logger;
    }
    public static set Logger(value) {
        TQInitParams._Logger = value;
    }

    public get logLevel() {
        if (TQInitParams._Logger == undefined) {
            TQInitParams._Logger = new TQLogger();
        }
        return TQInitParams._Logger.defaultLogLevel;
    }

    public set logLevel(value) {
        if (TQInitParams._Logger == undefined) {
            TQInitParams._Logger = new TQLogger();
        }
        TQInitParams._Logger.defaultLogLevel = value;
    }

    public get applicationFontsCollectionType(): tqtypes.ApplicationFontsType[] | tqtypes.ApplicationFontsType | undefined {
        return this._applicationFontsCollectionType;
    }
    public set applicationFontsCollectionType(value: tqtypes.ApplicationFontsType[] | tqtypes.ApplicationFontsType | undefined) {
        this._applicationFontsCollectionType = value;
    }

    public get fontBufferType(): tqtypes.FontBufferType {
        return this._fontBufferType;
    }
    public set fontBufferType(value: tqtypes.FontBufferType) {
        this._fontBufferType = value;
    }

    /** Which use events operations should be reported to server, by default only APPLY. Other possible options are BOTH_OPEN_AND_APPLY, ONLY_OPEN */
    private _recentsUpdatePreference = tqtypes.RecentsUpdatePreference.ONLY_APPLY;

    public get recentsUpdatePreference() {
        return this._recentsUpdatePreference;
    }
    public set recentsUpdatePreference(value) {
        this._recentsUpdatePreference = value;
    }

    /* Recent fonts update callback */
    private _recentFontsUpdateCallback = (fonts: TQFont[]) => {
        return true;
    };

    public getLanguage(): tqtypes.Language | undefined {
        return TQInitParams._lang;
    }

    public static setLanguage(value: tqtypes.Language) {
        TQInitParams._lang = value;
    }

    public get fontsUpdateCallback(): ((updates: tqtypes.FontUpdateNotification[]) => boolean) | undefined {
        return this._fontsUpdateCallback;
    }
    public set fontsUpdateCallback(value: ((updates: tqtypes.FontUpdateNotification[]) => boolean) | undefined) {
        this._fontsUpdateCallback = value;
    }

    public get selectionPollInterval() {
        return this._selectionPollInterval;
    }
    public set selectionPollInterval(value) {
        this._selectionPollInterval = value;
    }

    public static getAppLanguage(): tqtypes.Language | undefined {
        return TQInitParams._lang;
    }

    public static get cachedEntitlement(): tqtypes.Entitlement | undefined {
        return TQInitParams._cachedEntitlement;
    }
    public static set cachedEntitlement(value: tqtypes.Entitlement | undefined) {
        TQInitParams._cachedEntitlement = value;
    }

    public get watchFontUpdates(): tqtypes.FontType[] {
        return this._watchFontUpdates;
    }
    public set watchFontUpdates(value: tqtypes.FontType[]) {
        this._watchFontUpdates = value;
    }

    constructor(authParams: tqtypes.AuthParams) {
        this._authParams = authParams;
    }

    public get appEntitlement(): tqtypes.AppEntitlement {
        return this._appEntitlement;
    }
    public set appEntitlement(value: tqtypes.AppEntitlement) {
        this._appEntitlement = value;
    }

    public get runtimeMode(): tqtypes.RuntimeMode {
        return this._runtimeMode;
    }
    public set runtimeMode(value: tqtypes.RuntimeMode) {
        this._runtimeMode = value;
    }

    public get authParams(): tqtypes.AuthParams {
        return this._authParams;
    }
    public set authParams(value: tqtypes.AuthParams) {
        this._authParams = value;
    }

    public get fontFetchStrategy(): tqtypes.FontFetchStrategy {
        return this._fontFetchStrategy;
    }
    public set fontFetchStrategy(value: tqtypes.FontFetchStrategy) {
        this._fontFetchStrategy = value;
    }

    public get diskCache(): tqtypes.DiskCacheType {
        return this._diskCacheType;
    }
    public set diskCache(value: tqtypes.DiskCacheType) {
        this._diskCacheType = value;
    }

    public get statusUpdateCallback(): (status: tqtypes.StatusCode, error: tqtypes.Error) => boolean {
        return this._statusUpdateCallback;
    }
    public set statusUpdateCallback(value: (status: tqtypes.StatusCode, error: tqtypes.Error) => boolean) {
        this._statusUpdateCallback = value;
    }

    public get recentFontsUpdateCallback() {
        return this._recentFontsUpdateCallback;
    }
    public set recentFontsUpdateCallback(value) {
        this._recentFontsUpdateCallback = value;
    }

    public get fontDownloadCallback(): tqtypes.FontDownloadCallback {
        return this._fontDownloadCallback;
    }
    public set fontDownloadCallback(value: tqtypes.FontDownloadCallback) {
        this._fontDownloadCallback = value;
    }

    public get timeOutDurationInMillis(): number {
        return this._timeOutDurationInMillis;
    }
    public set timeOutDurationInMillis(value: number) {
        this._timeOutDurationInMillis = value;
    }
}
